import { z } from "zod";

/**
 * Common error schema for RPC responses
 */
export const ErrorSchema = z.object({
  code: z.string(),
  message: z.string(),
  details: z.record(z.string(), z.unknown()).optional(),
});

/**
 * Standard success response wrapper
 */
export const SuccessResponseSchema = z.object({
  success: z.literal(true),
  data: z.unknown(),
});

/**
 * Standard error response wrapper
 */
export const ErrorResponseSchema = z.object({
  success: z.literal(false),
  error: ErrorSchema,
});

/**
 * Standard RPC response that can be either success or error
 */
export const RpcResponseSchema = z.union([
  SuccessResponseSchema,
  ErrorResponseSchema,
]);

/**
 * Pagination input schema
 */
export const PaginationInputSchema = z.object({
  page: z.int().min(1).prefault(1),
  limit: z.int().min(1).max(100).prefault(10),
});

/**
 * Pagination info for responses
 */
export const PaginationInfoSchema = z.object({
  page: z.int(),
  limit: z.int(),
  total: z.int(),
  hasNext: z.boolean(),
  hasPrev: z.boolean(),
});

export type ErrorType = z.infer<typeof ErrorSchema>;
export type SuccessResponse<T = unknown> = z.infer<
  typeof SuccessResponseSchema
> & { data: T };
export type ErrorResponse = z.infer<typeof ErrorResponseSchema>;
export type RpcResponse<T = unknown> = SuccessResponse<T> | ErrorResponse;
export type PaginationInput = z.infer<typeof PaginationInputSchema>;
export type PaginationInfo = z.infer<typeof PaginationInfoSchema>;

/**
 * Lenient enum schema for output validation.
 * Normalizes case and falls back to null for unrecognized values,
 * so one bad DB row doesn't fail the entire list response.
 *
 * Use in output schemas where data comes from the DB and may have
 * been written by external sources (OCR, integrations, migrations).
 */
export function lenientEnum(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  schema: z.ZodEnum<any>,
) {
  return z
    .string()
    .transform((v) => v.toLowerCase())
    .pipe(schema)
    .nullable()
    .catch(() => null);
}
